import { useCallback, useEffect, useState } from "react";
import { Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SOURCE_LABEL = {
    island_gold: "Island Gold",
    affiliate: "Affiliate",
    vendor: "Vendor",
    partner: "Partner",
};

function OfferingRow({ o, onSaved }) {
    const [open, setOpen] = useState(false);
    const [url, setUrl] = useState(o.buy_url?.includes("example-affiliate") ? "" : o.buy_url || "");
    const [pct, setPct] = useState(o.commission_pct ?? "");
    const [disc, setDisc] = useState(o.disclosure || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/offerings/${o.key}`, {
                buy_url: url.trim(),
                commission_pct: pct === "" ? null : Number(pct),
                disclosure: disc,
            });
            toast.success(`${o.name} saved`);
            setOpen(false);
            onSaved();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Check the link and commission");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="py-3 border-b border-forest/10" data-testid={`offering-row-${o.key}`}>
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                    <p className="text-forest font-medium">{o.name}</p>
                    <p className="text-xs text-brown">
                        {SOURCE_LABEL[o.source_type] || o.source_type} · {o.seller_name}
                        {o.commission_pct != null && <> · {o.commission_pct}% commission</>}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        className={`text-[0.6rem] tracking-[0.25em] uppercase px-2 py-1 rounded-full ${o.public ? "bg-olive/20 text-olive" : "bg-gold/15 text-gold"}`}
                        data-testid={`offering-status-${o.key}`}
                    >
                        {o.public ? "Live on site" : "Hidden · coming soon"}
                    </span>
                    {o.editable && (
                        <button className="btn-pill btn-ghost text-[0.6rem]" onClick={() => setOpen((v) => !v)}
                            data-testid={`offering-edit-${o.key}`}>
                            {open ? "Close" : "Add link"}
                        </button>
                    )}
                </div>
            </div>
            {o.needs.length > 0 && (
                <p className="mt-1 text-xs text-brown" data-testid={`offering-needs-${o.key}`}>
                    Still needs: {o.needs.join(" · ")}
                </p>
            )}
            {open && (
                <div className="mt-3 grid md:grid-cols-[2fr_110px_2fr_auto] gap-2">
                    <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://real-affiliate-link…"
                        className="bg-transparent border border-forest/25 rounded-sm px-3 py-2 text-sm text-forest"
                        data-testid={`offering-url-${o.key}`} />
                    <input value={pct} onChange={(e) => setPct(e.target.value)} placeholder="% comm." type="number"
                        className="bg-transparent border border-forest/25 rounded-sm px-3 py-2 text-sm text-forest"
                        data-testid={`offering-commission-${o.key}`} />
                    <input value={disc} onChange={(e) => setDisc(e.target.value)} placeholder="Disclosure line"
                        className="bg-transparent border border-forest/25 rounded-sm px-3 py-2 text-sm text-forest"
                        data-testid={`offering-disclosure-${o.key}`} />
                    <button onClick={save} disabled={saving || !url} className="btn-pill btn-primary text-[0.6rem]"
                        data-testid={`offering-save-${o.key}`}>
                        {saving ? <Loader2 size={12} className="animate-spin" /> : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}

export default function AdminOfferings() {
    const [rows, setRows] = useState(null);
    const load = useCallback(() => {
        api.get("/admin/offerings")
            .then(({ data }) => setRows(data.offerings))
            .catch(() => toast.error("Could not load offerings"));
    }, []);
    useEffect(load, [load]);

    return (
        <div className="mt-10 p-6 md:p-8 rounded-sm border border-forest/15 bg-cream-warm" data-testid="admin-offerings">
            <span className="divider-rule text-brown"><Package size={12} /> Offerings</span>
            <h2 className="font-serif text-3xl text-forest mt-3">Everything for sale</h2>
            <p className="mt-2 text-sm text-brown max-w-2xl">
                Outside items stay hidden until they have a real link and a disclosure line.
                Picks also need to be approved in the picks queue.
            </p>
            {!rows ? <Loader2 className="animate-spin mt-6" size={18} /> : (
                <div className="mt-6">
                    {rows.map((o) => <OfferingRow key={o.key} o={o} onSaved={load} />)}
                </div>
            )}
        </div>
    );
}
